import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { firebaseService } from '../../services/firebase';
import { auth } from '../../services/firebase'; // Import auth directly
import toast from 'react-hot-toast';

const EmailVerification = () => {
  const { user, resendEmailVerification, signOut, refreshUserStatus, fetchUserData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const navigate = useNavigate();

  // Check email verification status periodically
  useEffect(() => {
    let interval;
    
    console.log('EmailVerification: Setting up verification check...', { 
      user: !!user, 
      isAnonymous: user?.isAnonymous, 
      emailVerified: user?.emailVerified 
    });
    
    if (user && !user.isAnonymous && !user.emailVerified) {
      setCheckingVerification(true);
      console.log('EmailVerification: Starting verification polling...');
      
      interval = setInterval(async () => {
        console.log('EmailVerification: Checking verification status...');
        try {
          // Reload Firebase user to get latest verification status
          if (auth.currentUser) {
            await auth.currentUser.reload();
            const currentUser = firebaseService.getCurrentUser();
            
            console.log('EmailVerification: Current user verification status:', currentUser?.emailVerified);
            
            if (currentUser && currentUser.emailVerified) {
                console.log('EmailVerification: Email verified! Redirecting...');
                setCheckingVerification(false);
                clearInterval(interval);
                
                await refreshUserStatus();
                const updatedUser = await fetchUserData();
                
                console.log('Updated backend user after verification:', updatedUser);
                
                toast.success('Email verified successfully!');
                navigate('/dashboard');
            }
          }
        } catch (error) {
          console.error('EmailVerification: Error checking email verification:', error);
        }
      }, 3000); // Check every 3 seconds
    } else {
      setCheckingVerification(false);
    }
    
    return () => {
      console.log('EmailVerification: Cleaning up verification check...');
      if (interval) {
        clearInterval(interval);
        setCheckingVerification(false);
      }
    };
  }, [user?.emailVerified, user?.isAnonymous, refreshUserStatus, fetchUserData, navigate]); // Added more dependencies for safety

  const handleResendVerification = async () => {
    setLoading(true);
    await resendEmailVerification();
    setLoading(false);
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100 flex justify-center items-center p-4">
      <div className="w-full max-w-md">
        <div className="authorization-form">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLineJoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify Your Email</h1>
            <p className="text-gray-600">We've sent a verification email to:</p>
            <p className="font-semibold text-gray-900">{user?.email}</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800 mb-3">
              Please check your email and click the verification link to complete your account setup.
            </p>
            <p className="text-xs text-yellow-700">
              Don't forget to check your spam folder if you don't see the email.
            </p>
            {checkingVerification && (
              <div className="flex items-center mt-3 text-xs text-yellow-700">
                <div className="loading-spinner" style={{ width: '12px', height: '12px', marginRight: '6px' }}></div>
                Checking for verification...
              </div>
            )}
          </div>

          <button
            onClick={handleResendVerification}
            disabled={loading}
            className="authorization-button-submit mb-4"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="loading-spinner"></div>
                <span>Sending...</span>
              </div>
            ) : (
              'Resend Verification Email'
            )}
          </button>

          <div className="text-center">
            <button
              onClick={handleSignOut}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Sign out and use a different email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;